import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const featured = "door-arch";
const items = [
  "sculpture",
  "carving-detail",
  "door-arched-glass",
  "door-interior",
];

export const Gallery = ({ t }) => (
  <section id="works" data-testid="gallery-section" className="pt-12 lg:pt-16 pb-24 lg:pb-32 bg-[#F5F2EC]">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="flex flex-wrap items-end justify-between gap-8"
      >
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#A67C00] mb-4">
            {t.gallery.overline}
          </p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-[#2D1810] tracking-tight">{t.gallery.title}</h2>
          <p className="mt-4 text-base sm:text-lg text-[#3E2723]/70">{t.gallery.subtitle}</p>
        </div>
        <a
          href="#quote"
          data-testid="gallery-cta"
          className="inline-flex items-center gap-2 border border-[#3E2723] text-[#3E2723] px-6 py-3 text-sm font-semibold rounded-sm hover:bg-[#3E2723] hover:text-[#F5F2EC] transition-colors duration-300"
        >
          {t.gallery.cta}
          <ArrowRight size={16} />
        </a>
      </motion.div>

      <div className="mt-16">
        <motion.figure
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          data-testid={`gallery-item-${featured}`}
          className="group"
        >
          <div className="overflow-hidden rounded-sm aspect-video">
            <img
              src={`/images/${featured}.webp`}
              alt={t.gallery.items[featured]}
              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
            />
          </div>
          <figcaption className="mt-3 flex items-baseline gap-3">
            <span className="text-xs font-semibold tracking-[0.15em] text-[#A67C00]">01</span>
            <span className="text-sm sm:text-base font-medium text-[#3E2723]">{t.gallery.items[featured]}</span>
          </figcaption>
        </motion.figure>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 sm:gap-x-6 lg:gap-x-8 items-start">
          {items.map((key, i) => (
            <motion.figure
              key={key}
              initial={{ opacity: 0, clipPath: "inset(14% 10% 14% 10%)" }}
              whileInView={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: (i % 2) * 0.12, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`gallery-item-${key}`}
              className="group"
            >
              <div
                onMouseMove={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                }}
                className="spotlight-frame overflow-hidden rounded-sm ring-1 ring-[#D4AF37]/25 aspect-[2/3]"
              >
                <img
                  src={`/images/${key}.webp`}
                  alt={t.gallery.items[key]}
                  loading="lazy"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <figcaption className="mt-2.5 flex items-baseline gap-2.5">
                <span className="text-[10px] sm:text-xs font-semibold tracking-[0.15em] text-[#A67C00]">
                  {String(i + 2).padStart(2, "0")}
                </span>
                <span className="text-xs sm:text-sm font-medium text-[#3E2723] leading-snug">
                  {t.gallery.items[key]}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </div>
  </section>
);
